import tw from 'tailwind-styled-components'
import {cn} from '@containers/html-utils'
import {Size, TwSelectType, Variant} from './types'

const getDefaultClass = (p: TwSelectType): string => {
  if (p.$link) {
    return 'placeholder-grey-l20'
  } else if (p.$variant === 'default') {
    return cn(
      'text-primary',
      'placeholder-grey-l20',
      'bg-grey-l100',
      'border',
      'border-solid',
      'border-grey-l60',
      'hover:border-interactive-l60'
    )
  } else if (p.$variant === 'panel') {
    return cn(
      'text-panel-text-primary',
      'placeholder-panel-text-placeholder',
      'bg-panel-background-constant',
      'shadow-border-1',
      p.$error ? 'shadow-status-error' : 'shadow-panel-line-base'
    )
  } else if (p.$variant === 'new') {
    return cn(
      'text-primary',
      'placeholder-placeholder-new',
      p.$bgColor ?? 'bg-background-constant',
      'shadow-border-1',
      p.$error ? 'shadow-status-error' : 'shadow-line-base'
    )
  } else {
    throw new Error('Unsupported default class state')
  }
}

const getActiveClass = (p: TwSelectType): string => {
  if (p.$link) {
    return 'placeholder-grey-l20'
  } else if (p.$variant === 'default') {
    return cn(
      'text-primary',
      'placeholder-grey-l20',
      'outline',
      'outline-2',
      'outline-interactive-l80',
      'border',
      'border-solid',
      'border-interactive-l60',
      'bg-grey-l100'
    )
  } else if (p.$variant === 'panel') {
    return cn(
      'text-panel-text-primary',
      'placeholder-panel-text-placeholder',
      'outline',
      'outline-1',
      'outline-offset-0',
      'shadow-border-1',
      p.$error
        ? cn('outline-status-error', 'shadow-status-error')
        : cn('outline-action-default', 'shadow-action-default'),
      'bg-panel-background-constant'
    )
  } else if (p.$variant === 'new') {
    return cn(
      'text-primary',
      'placeholder-placeholder-new',
      'outline',
      'outline-1',
      'outline-offset-0',
      'shadow-border-1',
      p.$error
        ? cn('outline-status-error', 'shadow-status-error')
        : cn('outline-action-default', 'shadow-action-default'),
      p.$bgColor ?? 'bg-background-constant'
    )
  } else {
    throw new Error('Unsupported default active class state')
  }
}

const getPlaceholderClass = (p: TwSelectType): string => {
  if (p.$link) {
    return cn('text-placeholder', 'placeholder-grey-l20')
  } else if (p.$variant === 'default') {
    return cn(
      'text-placeholder',
      'placeholder-grey-l20',
      'border',
      'border-solid',
      'hover:border-interactive-l60',
      'border-grey-l60',
      'bg-grey-l100'
    )
  } else if (p.$variant === 'panel') {
    return cn(
      'text-panel-text-placeholder',
      'placeholder-panel-text-placeholder',
      'shadow-border-1',
      'hover:shadow-interactive-l60',
      'shadow-grey-l60',
      'bg-panel-background-constant'
    )
  } else if (p.$variant === 'new') {
    return cn(
      'text-placeholder-new',
      'placeholder-placeholder-new',
      'shadow-border-1',
      'hover:shadow-action-default',
      'shadow-line-base',
      p.$bgColor ?? 'bg-background-constant',
      p.$error ? 'shadow-status-error' : 'shadow-action-default'
    )
  } else {
    throw new Error('Unsupported default placeholder state')
  }
}

const getActivePlaceholderClass = (p: TwSelectType): string => {
  if (p.$link) {
    return cn('text-placeholder', 'placeholder-grey-l20')
  } else if (p.$variant === 'default') {
    return cn(
      'text-placeholder',
      'placeholder-grey-l20',
      'outline',
      'outline-2',
      'outline-interactive-l80',
      'border',
      'border-solid',
      'border-interactive-l60',
      'bg-grey-l10'
    )
  } else if (p.$variant === 'panel') {
    return cn(
      'text-panel-text-placeholder',
      'placeholder-panel-text-placeholder',
      'outline',
      'outline-2',
      'outline-interactive-l80',
      'shadow-border-1',
      'shadow-interactive-l60',
      'bg-panel-background-constant'
    )
  } else if (p.$variant === 'new') {
    return cn(
      'text-secondary',
      'placeholder-placeholder-new',
      'outline',
      'outline-2',
      'outline-action-default',
      'shadow-border-1',
      'shadow-action-default',
      p.$bgColor ?? 'bg-background-constant',
      p.$error ? 'shadow-status-error' : 'shadow-action-default'
    )
  } else {
    throw new Error('Unsupported active placeholder state')
  }
}

const getDisabledClass = (p: TwSelectType): string => {
  if (p.$link) {
    return cn('text-grey-l20', 'pointer-events-none', 'placeholder-grey-l20')
  } else if (p.$variant === 'default') {
    return cn(
      'border',
      'border-solid',
      'bg-grey-l80',
      'border-grey-l60',
      'placeholder-grey-l20'
    )
  } else if (p.$variant === 'new' || p.$variant === 'panel') {
    return cn(
      'shadow-border-1',
      'bg-background-disabled',
      'shadow-grey-l60',
      'placeholder-grey-l20',
      'text-placeholder'
    )
  }
  return ''
}

const getState = (p: TwSelectType): string => {
  if (p.$disabled) {
    return getDisabledClass(p)
  } else if (p.$placeholder && p.$active) {
    return getActivePlaceholderClass(p)
  } else if (p.$placeholder) {
    return getPlaceholderClass(p)
  } else if (p.$active) {
    return getActiveClass(p)
  } else {
    return getDefaultClass(p)
  }
}

const getSize = (size: Size): string => {
  if (size === 'small') {
    return 'text-xs-regular h-32'
  } else if (size === 'medium') {
    return 'text-sm-regular h-32'
  } else if (size === 'big') {
    return 'text-sm-regular h-40'
  } else {
    throw new Error(`Select size is not implemented: ${size}`)
  }
}

export const TwSelect = tw.div<TwSelectType>`
  flex
  justify-between
  gap-8
  w-full
  overflow-hidden
  items-center
  ${p =>
    cn(
      p.$variant === 'default' &&
        p.$link &&
        cn(
          'text-action-default',
          'hover:text-action-hover',
          'active:text-action-pressed'
        ),
      p.$variant === 'default' && !p.$link && cn('rounded-4', 'px-12'),
      p.$variant === 'panel' && cn('rounded-8', 'px-12'),
      p.$variant === 'new' && cn('rounded-8', 'px-12'),
      getSize(p.$size ?? 'medium'),
      getState(p)
    )}
  ${p => (p.$disabled ? 'cursor-default' : 'cursor-pointer')}
`

export const TwWrap = tw.div`
  flex 
  gap-8 
  items-start
  flex-col
`

const defaultLabelClass = `
  text-grey-d80
`

const disabledLabelClass = `
  text-grey-l40
`

export const TwLabel = tw.div<TwSelectType>`
  plain-text-semibold
  truncate
  w-full
  ${p => (p.$disabled ? disabledLabelClass : defaultLabelClass)}
`

export const TwAvatar = tw.span`
  flex
  shrink-0
  self-center
`

export const TwDot = tw.span`
  text-success
  flex
  shrink-0
  self-center
`

export const TwIcon = tw.span`
  text-grey-l20
  flex
  shrink-0
  self-center
`

export const TwArrow = tw.div<{
  $rotate: boolean
  $disabled: boolean
  $variant: Variant
  $link: boolean
}>`
  ${p => {
    const cx: string[] = []
    if (p.$disabled) {
      if (p.$variant === 'default') {
        cx.push('text-grey-l20')
      } else if (p.$variant === 'panel') {
        cx.push('text-panel-text-disabled')
      } else if (p.$variant === 'new') {
        cx.push('text-disabled-new')
      }
    } else {
      if (p.$rotate) {
        cx.push('rotate-180', 'text-action-pressed')
      } else {
        if (p.$variant === 'default') {
          cx.push(p.$link ? 'text-action-default' : 'text-secondary')
        } else if (p.$variant === 'panel') {
          cx.push('text-panel-icon-secondary')
        } else if (p.$variant === 'new') {
          cx.push('text-secondary')
        }
      }
    }
    return cx.join(' ')
  }}
  flex
  self-center
  pointer-events-none
  shrink-0
`

export const TwChildren = tw.span<{$truncate?: boolean}>`
  pointer-events-none
  select-none
  w-full
  ${p => cn(p.$truncate && 'truncate')}
`
